// src/modules/Admin/containers/OnboardingPension/OnboardingPension.slice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { fetchPensionConfig, savePensionConfig } from '../../services/onboarding'
import { getUserMenus } from '../../modules/Auth/providers/services/authSupabase'
import { setMenus } from '../AccessControlList/acl.slice'
import { getLogger } from '../../core/debuggers'

const logger = getLogger('OnboardingPension.slice.js')

const initialState = {
  config: null,           // pension configuration object
  fetchStatus: 'idle',    // idle | loading | succeeded | failed
  updateStatus: 'idle',   // idle | loading | succeeded | failed
  error: null,
}

export const getPensionConfig = createAsyncThunk(
  'onboarding/fetch',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState()
      const userId = state.adminAuth.user?.id
      if (!userId) return rejectWithValue('User not authenticated')
      return await fetchPensionConfig(userId)
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message)
    }
  }
)

export const setPensionConfig = createAsyncThunk(
  'onboarding/update',
  async (updates, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState()
      const userId = state.adminUserProfile.profile.id
      
      if (!userId) return rejectWithValue('User not authenticated')
      
      const saved = await savePensionConfig({
        userId,
        method: updates.method,
        amount: updates.amount,
        percentage: updates.percentage,
        paymentDay: updates.paymentDay,
      })

      // Reload menus from DB so UTM visibility matches the new method immediately.
      try {
        const authUserId = state.adminAuth.user?.id || userId
        const menus = await getUserMenus(authUserId)
        dispatch(setMenus(menus))
      } catch (menuError) {
        logger.error('Failed to refresh menus after pension config save:', menuError)
      }

      return saved
    } catch (error) {
      logger.error(error)
      return rejectWithValue(error.message)
    }
  }
)

const pensionSlice = createSlice({
  name: 'adminOnboarding',
  initialState,
  reducers: {
    resetPensionState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      // GET config
      .addCase(getPensionConfig.pending, (state) => {
        state.fetchStatus = 'loading'
        state.error = null
      })
      .addCase(getPensionConfig.fulfilled, (state, action) => {
        state.config = action.payload || null
        state.fetchStatus = 'succeeded'
      })
      .addCase(getPensionConfig.rejected, (state, action) => {
        state.fetchStatus = 'failed'
        state.error = action.payload === 'User not authenticated'
          ? null // auth flow lo maneja otro slice
          : action.payload || 'Failed to fetch pension config'
      })

      // UPDATE config
      .addCase(setPensionConfig.pending, (state) => {
        state.updateStatus = 'loading'
        state.error = null
      })
      .addCase(setPensionConfig.fulfilled, (state, action) => {
        state.config = action.payload
        state.updateStatus = 'succeeded'
      })
      .addCase(setPensionConfig.rejected, (state, action) => {
        state.updateStatus = 'failed'
        state.error = action.payload || 'Failed to update pension config'
      })
  }
})

export const { resetPensionState } = pensionSlice.actions

export default pensionSlice.reducer
